// apps/web/src/api/refreshLock.ts
// Cross-tab serialization for the `/api/auth/refresh` critical section.
// Primary path: Web Locks API. Fallback: localStorage lease with a naive
// CAS via post-write read verification.
//
// @aiPurpose ensure only one tab in a browser profile sends
//   `POST /api/auth/refresh` at a time. Sequential refreshes are correct
//   because the server one-time-token rotation produces a fresh cookie the
//   next tab will use.
// @aiInvariant lock metadata MUST contain only non-secret fields:
//   `{ ownerId, leaseUntilMs, generation }`. No tokens, cookies, user data.
// @aiSecurity refusing the lock is not a security failure — at worst we
//   degrade to per-tab single-flight behavior. The lock prevents
//   *concurrent* refresh-token reuse, not unauthorized access.
const LOCK_NAME = 'magimp:auth-refresh:v1';
const STORAGE_KEY = 'magimp:auth-refresh-lock:v1';
const LEASE_MS = 10_000;
const POLL_MS = 50;
// Defensive ceiling. The 10 s fetch timeout in `refresh.ts` releases a
// healthy holder long before this fires; reaching it means the lock storage
// is wedged (e.g. a JS exception in the holder before `clearMeta`).
const MAX_WAIT_MS = 30_000;

type LockMeta = {
  ownerId: string;
  leaseUntilMs: number;
  generation: number;
};

const ALLOWED_META_KEYS = new Set(['ownerId', 'leaseUntilMs', 'generation']);

function hasWebLocks(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'locks' in navigator &&
    navigator.locks != null &&
    typeof navigator.locks.request === 'function'
  );
}

function tryParseMeta(raw: string | null): LockMeta | null {
  if (raw == null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed == null || typeof parsed !== 'object') return null;
    const o = parsed as Record<string, unknown>;
    if (typeof o.ownerId !== 'string' || o.ownerId === '') return null;
    if (typeof o.leaseUntilMs !== 'number' || !Number.isFinite(o.leaseUntilMs)) return null;
    if (typeof o.generation !== 'number' || !Number.isFinite(o.generation)) return null;
    // INVARIANT: reject any unknown key — keeps the wire shape locked and
    // makes "no secrets in metadata" a parse-time guarantee.
    for (const k of Object.keys(o)) {
      if (!ALLOWED_META_KEYS.has(k)) return null;
    }
    return { ownerId: o.ownerId, leaseUntilMs: o.leaseUntilMs, generation: o.generation };
  } catch {
    return null;
  }
}

function readMeta(): LockMeta | null {
  try {
    return tryParseMeta(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeMeta(meta: LockMeta): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(meta));
    return true;
  } catch {
    return false;
  }
}

function clearMeta(ownerId: string): void {
  try {
    const cur = readMeta();
    if (cur && cur.ownerId === ownerId) {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

function genOwnerId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `t${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

async function withFallbackLock<T>(critical: () => Promise<T>): Promise<T> {
  const ownerId = genOwnerId();
  const start = Date.now();

  // Probe storage availability once. If unavailable (private mode quota,
  // disabled storage), degrade securely to no cross-tab protection — the
  // per-tab `inFlight` mutex still keeps this tab correct.
  try {
    localStorage.getItem(STORAGE_KEY);
  } catch {
    return await critical();
  }

  // Storage-event wake-up so a holder's release (clearMeta -> removeItem)
  // unblocks waiters quickly without paying full POLL_MS latency. The
  // poll fallback stays in place for the rare browsers that miss the
  // event or for environments that suppress same-origin notifications.
  let pendingWake: (() => void) | null = null;
  const wakeOnRelease = (e: StorageEvent): void => {
    if (e.key !== STORAGE_KEY) return;
    const wake = pendingWake;
    pendingWake = null;
    if (wake) wake();
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', wakeOnRelease);
  }

  try {
    for (;;) {
      const now = Date.now();
      const cur = readMeta();
      const isStale = cur === null || cur.leaseUntilMs <= now;
      if (isStale) {
        const generation = cur ? cur.generation + 1 : 1;
        const meta: LockMeta = { ownerId, leaseUntilMs: now + LEASE_MS, generation };
        const wrote = writeMeta(meta);
        if (!wrote) {
          return await critical();
        }
        // Naive CAS: re-read, accept ownership only if our write survived.
        const verify = readMeta();
        if (verify && verify.ownerId === ownerId && verify.generation === generation) {
          try {
            return await critical();
          } finally {
            clearMeta(ownerId);
          }
        }
        // Lost the race — fall through to wait/retry.
      }
      if (Date.now() - start > MAX_WAIT_MS) {
        // INVARIANT: degrade securely. A healthy holder's critical section
        // can never run past the 10 s refresh fetch timeout in
        // `refresh.ts`, so reaching MAX_WAIT_MS implies the storage is
        // wedged or being thrashed by a tight handover loop. We do NOT
        // force-clear the metadata — there's no atomic remove, and any
        // remove racing with a fresh holder's CAS write would clobber a
        // valid lease. Instead fall through to the same path used when
        // storage is unavailable: run `critical` without cross-tab
        // protection (per-tab `inFlight` mutex still applies). Cross-tab
        // collisions become best-effort under wedged storage; the spec
        // explicitly allows this degraded mode.
        return await critical();
      }
      // Wait for either a storage event (fast wake on holder release)
      // or the POLL_MS fallback timer, whichever fires first.
      await new Promise<void>((resolve) => {
        const timer = setTimeout(() => {
          pendingWake = null;
          resolve();
        }, POLL_MS);
        pendingWake = () => {
          clearTimeout(timer);
          resolve();
        };
      });
    }
  } finally {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', wakeOnRelease);
    }
  }
}

/**
 * @aiPurpose run `critical` while holding the cross-tab refresh lock.
 *   Web Locks where available; localStorage lease otherwise. Returns
 *   whatever `critical` returns; rethrows whatever it throws. The lock is
 *   always released — even if `critical` throws or the holder times out.
 */
export async function withRefreshLock<T>(critical: () => Promise<T>): Promise<T> {
  if (hasWebLocks()) {
    return await navigator.locks.request(LOCK_NAME, () => critical());
  }
  return await withFallbackLock(critical);
}

/**
 * @internal Test-only: clear the fallback lease so each test starts from a
 *   known baseline. Production code never calls this.
 */
export function __resetRefreshLockForTesting(): void {
  if (import.meta.env.MODE !== 'test') {
    throw new Error('__resetRefreshLockForTesting is test-only');
  }
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export const __TESTING__ = {
  LOCK_NAME,
  STORAGE_KEY,
  LEASE_MS,
};
